import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx20T2iTurboParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 正向提示词，长度不超过2000字符
     */
    prompt: string;
    /**
     * 负向提示词，长度不超过500字符
     */
    negative_prompt?: string;
  };
  /**
   * 生成参数
   */
  parameters?: {
    /**
     * 图像尺寸，格式为'宽*高'，宽高必须在[512, 1440]范围内，最大分辨率1440*1440
     */
    size?: string;
    /**
     * 生成图像数量，范围1-4
     * @range 1 - 4
     */
    n?: number;
    /**
     * 随机种子，范围0-2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词扩展
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     */
    watermark?: boolean;
  };
}

export interface AlibabaWanx20T2iTurboResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx20_t2i_turbo
 *
 * Alibaba Wanx2.0 T2I Turbo Image Generator
 * 
 * Text-to-Image generation implementation for Alibaba Wanxiang 2.0 T2I Turbo API.
 * 
 * Model: wanx2.0-t2i-turbo
 * Features: 文本生成图像 (Text-to-Image)
 * Resolution: 宽高在[512, 1440]范围内，最大分辨率1440*1440
 * Batch Size: 1-4张图片
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/text-to-image-v2-api-reference
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx20T2iTurbo } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx20T2iTurbo({
 *   // 参数见 AlibabaWanx20T2iTurboParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx20T2iTurboParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx20T2iTurbo(
  params: AlibabaWanx20T2iTurboParams,
): Promise<AlibabaWanx20T2iTurboResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx20_t2i_turbo',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx20T2iTurboResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
